<template>
  <section>
    <div
      class="px-4 mx-auto max-w-3xl lg:px-0"
      :class="[
        paddingTop === 'reduced' ? 'pt-6' : 'pt-4 lg:pt-4',
        paddingBottom === 'reduced' ? 'pb-6' : 'pb-8 lg:pb-4'
      ]"
    >
      <div class="overflow-hidden rounded-xl border border-[#E0D5C5] bg-white">
        <div class="flex flex-col gap-4 justify-between items-start px-6 py-4 bg-[#3B2314] sm:flex-row sm:items-center">
          <div class="flex gap-3.5 items-center min-w-0">
            <img
              v-if="headerIconSrc && isHeaderIconSvg"
              :src="headerIconSrc"
              :alt="headerIconAlt"
              width="44"
              height="44"
              loading="lazy"
              class="shrink-0 size-11"
            />
            <nuxt-img
              v-else-if="headerIconSrc"
              :src="headerIconSrc"
              :alt="headerIconAlt"
              width="44"
              height="44"
              format="webp"
              loading="lazy"
              sizes="44px"
              class="shrink-0 size-11"
            />
            <div
              v-else
              class="shrink-0 rounded-lg size-11 bg-[#2A2A2A]"
              aria-hidden="true"
            />

            <div class="min-w-0">
              <p
                class="font-bold text-white text-22 leading-27 font-ww-bold"
                v-html="productTitle"
              />
              <p
                v-if="productSubtitle"
                class="text-xs tracking-wide text-[#C8A882] uppercase font-ww-regular"
                v-html="productSubtitle"
              />
            </div>
          </div>

          <a
            v-if="shopButtonHref"
            :href="shopButtonHref"
            :aria-label="shopButtonAriaLabel"
            class="inline-flex gap-2.5 items-center px-8 py-3 text-lg text-white whitespace-nowrap rounded-full md-max:mx-auto btn-hover-lift shrink-0 bg-lifeboost-red font-ww-bold hover:opacity-90"
            target="_blank"
            rel="noopener noreferrer"
          >
            <span v-html="shopButtonText" />
            <svg
              class="shrink-0 size-3.5"
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              aria-hidden="true"
            >
              <path
                d="M0.000273357 7.93934L10.5559 7.93934L6.5581 12.6702L8.04351 13.9294L13.9297 6.96471L8.04351 -2.57293e-07L6.5581 1.25922L10.5559 5.99008L0.000273443 5.99008L0.000273357 7.93934Z"
                fill="white"
              />
            </svg>
          </a>
        </div>

        <div class="p-6">
          <div class="flex flex-col gap-6 w-full lg:flex-row lg:gap-4 lg:items-center">
            <div
              class="flex-1 min-w-0 text-17 leading-6 text-[#4A4A4A] font-ww-regular [&_p+p]:mt-5 [&_a]:font-ww-bold [&_a]:underline [&_sup]:text-xs"
              v-html="introContent"
            />

            <div class="mx-auto w-full max-w-[295px] shrink-0 lg:mx-0">
              <nuxt-img
                v-if="productImageSrc"
                :src="productImageSrc"
                :alt="productImageAlt"
                :width="productImageWidth"
                :height="productImageHeight"
                format="webp"
                loading="lazy"
                :sizes="productImageSizes"
                class="object-cover mx-auto w-full max-w-[295px]"
                :class="productImageRounded ? 'rounded-2xl aspect-[295/347]' : 'aspect-[295/347]'"
              />
              <div
                v-else
                class="mx-auto w-full max-w-[295px] aspect-[295/347] bg-[#2A2A2A]"
                :class="productImageRounded ? 'rounded-2xl' : ''"
                aria-hidden="true"
              />
            </div>
          </div>

          <ul
            v-if="ingredients.length"
            class="grid grid-cols-1 gap-2.5 mt-6 sm:grid-cols-2"
          >
            <li
              v-for="(ingredient, index) in ingredients"
              :key="index"
              class="px-4 py-3 rounded-md border border-[#E0D5C5] bg-[#FAF6F0]"
            >
              <div class="flex gap-3.5 items-start">
                <div class="overflow-hidden shrink-0 rounded-lg border border-[#E0D5C5] size-[90px] bg-[#2A2A2A]">
                  <nuxt-img
                    v-if="ingredient.imageSrc"
                    :src="ingredient.imageSrc"
                    :alt="ingredient.imageAlt || ingredient.title"
                    width="90"
                    height="90"
                    format="webp"
                    loading="lazy"
                    sizes="90px"
                    class="object-cover w-full h-full"
                  />
                </div>

                <div class="min-w-0">
                  <p
                    class="text-15 font-bold leading-26 text-[#6B3A22] font-ww-bold"
                    v-html="ingredient.title"
                  />
                  <p
                    class="text-sm !leading-6 text-[#4A4A4A] font-ww-regular"
                    v-html="ingredient.description"
                  />
                </div>
              </div>
            </li>
          </ul>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
const mitocreamerIngredientsDefault = [
  {
    title: 'C8Vantage',
    description: 'a premium MCT oil known for providing a clean, easy source of fuel.',
    imageSrc: '/assets/images/mitocreamer-c8vantage.jpg',
    imageAlt: 'C8Vantage MCT oil'
  },
  {
    title: 'Mitoprime',
    description: 'Which supports overall cellular wellness and full body support.',
    imageSrc: '/assets/images/mitocreamer-mitoprime.jpg',
    imageAlt: 'Mitoprime powder'
  },
  {
    title: 'Grass Fed Bovine Collagen',
    description: 'Which provides added protein, beneficial for building muscle and thereby improving energy.',
    imageSrc: '/assets/images/mitocreamer-collagen.jpg',
    imageAlt: 'Grass fed bovine collagen powder'
  },
  {
    title: 'Electrolytes',
    description: 'Support and balance hydration levels (poor hydration leads to fatigue, weakness, and poor cellular functioning).',
    imageSrc: '/assets/images/mitocreamer-electrolytes.jpg',
    imageAlt: 'Electrolytes'
  }
]
</script>

<script setup>
const props = defineProps({
  headerIconSrc: {
    type: String,
    default: '/assets/images/mitocreamer-header-icon.svg'
  },
  headerIconAlt: {
    type: String,
    default: 'Mitocreamer icon'
  },
  productTitle: {
    type: String,
    default: 'Mitocreamer'
  },
  productSubtitle: {
    type: String,
    default: 'Functional Coffee Supplement'
  },
  shopButtonText: {
    type: String,
    default: 'Shop Now'
  },
  shopButtonHref: {
    type: String,
    default: 'https://lifeboostcoffee.com/products/new-mito-creamer'
  },
  introContent: {
    type: String,
    default: `<p><a href="https://lifeboostcoffee.com/products/new-mito-creamer" target="_blank" rel="noopener noreferrer"><strong>Lifeboost Mitocreamer</strong></a> was made with one thing in mind - mitochondrial health.</p>
<p>Mitochondrial health is foundational for energy production as these "cellular powerhouses" provide what is needed by the body for metabolism, muscle contraction, brain function, and hormone levels.</p>
<p>Poor mitochondrial health is often evidenced by muscle weakness, low testosterone, and you guessed it, low energy.<sup>28</sup></p>
<p>Mitocreamer contains:</p>`
  },
  productImageSrc: {
    type: String,
    default: '/assets/images/mitocreamer-product.png'
  },
  productImageAlt: {
    type: String,
    default: 'Lifeboost Mitocreamer functional coffee supplement pouch'
  },
  productImageWidth: {
    type: Number,
    default: 295
  },
  productImageHeight: {
    type: Number,
    default: 347
  },
  productImageRounded: {
    type: Boolean,
    default: true
  },
  ingredients: {
    type: Array,
    default: () => mitocreamerIngredientsDefault
  },
  paddingTop: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  },
  paddingBottom: {
    type: String,
    default: 'reduced',
    validator: (value) => ['default', 'reduced'].includes(value)
  }
})

const isHeaderIconSvg = computed(() => props.headerIconSrc?.toLowerCase().endsWith('.svg'))

const stripRichText = (value) => (value ?? '').replace(/<[^>]*>/g, '').trim()

const shopButtonAriaLabel = computed(() => {
  const buttonText = stripRichText(props.shopButtonText) || 'Shop Now'
  const productName = stripRichText(props.productTitle)

  return productName ? `${buttonText} - ${productName}` : buttonText
})

const productImageSizes = computed(() => `xs:100vw sm:${props.productImageWidth}px lg:${props.productImageWidth}px`)

defineBuilderComponent({
  friendlyName: 'Content - Showcase Mitocreamer',
  inputs: [
    {
      name: 'headerIconSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp', 'svg'],
      defaultValue: '/assets/images/mitocreamer-header-icon.svg'
    },
    {
      name: 'headerIconAlt',
      type: 'text',
      defaultValue: 'Mitocreamer icon'
    },
    {
      name: 'productTitle',
      type: 'richText',
      defaultValue: 'Mitocreamer'
    },
    {
      name: 'productSubtitle',
      type: 'text',
      defaultValue: 'Functional Coffee Supplement'
    },
    {
      name: 'shopButtonText',
      type: 'text',
      defaultValue: 'Shop Now'
    },
    {
      name: 'shopButtonHref',
      type: 'text',
      defaultValue: 'https://lifeboostcoffee.com/products/new-mito-creamer'
    },
    {
      name: 'introContent',
      type: 'richText',
      defaultValue: `<p><a href="https://lifeboostcoffee.com/products/new-mito-creamer" target="_blank" rel="noopener noreferrer"><strong>Lifeboost Mitocreamer</strong></a> was made with one thing in mind - mitochondrial health.</p>
<p>Mitochondrial health is foundational for energy production as these "cellular powerhouses" provide what is needed by the body for metabolism, muscle contraction, brain function, and hormone levels.</p>
<p>Poor mitochondrial health is often evidenced by muscle weakness, low testosterone, and you guessed it, low energy.<sup>28</sup></p>
<p>Mitocreamer contains:</p>`
    },
    {
      name: 'productImageSrc',
      type: 'file',
      allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
      defaultValue: '/assets/images/mitocreamer-product.png'
    },
    {
      name: 'productImageAlt',
      type: 'text',
      defaultValue: 'Lifeboost Mitocreamer functional coffee supplement pouch'
    },
    {
      name: 'productImageRounded',
      type: 'boolean',
      defaultValue: true
    },
    {
      name: 'ingredients',
      type: 'list',
      defaultValue: [
        {
          title: 'C8Vantage',
          description: 'a premium MCT oil known for providing a clean, easy source of fuel.',
          imageSrc: '/assets/images/mitocreamer-c8vantage.jpg',
          imageAlt: 'C8Vantage MCT oil'
        },
        {
          title: 'Mitoprime',
          description: 'Which supports overall cellular wellness and full body support.',
          imageSrc: '/assets/images/mitocreamer-mitoprime.jpg',
          imageAlt: 'Mitoprime powder'
        },
        {
          title: 'Grass Fed Bovine Collagen',
          description: 'Which provides added protein, beneficial for building muscle and thereby improving energy.',
          imageSrc: '/assets/images/mitocreamer-collagen.jpg',
          imageAlt: 'Grass fed bovine collagen powder'
        },
        {
          title: 'Electrolytes',
          description: 'Support and balance hydration levels (poor hydration leads to fatigue, weakness, and poor cellular functioning).',
          imageSrc: '/assets/images/mitocreamer-electrolytes.jpg',
          imageAlt: 'Electrolytes'
        }
      ],
      subFields: [
        {
          name: 'imageSrc',
          type: 'file',
          allowedFileTypes: ['jpeg', 'jpg', 'png', 'webp'],
          defaultValue: ''
        },
        {
          name: 'imageAlt',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'title',
          type: 'text',
          defaultValue: ''
        },
        {
          name: 'description',
          type: 'longText',
          defaultValue: ''
        }
      ]
    },
    {
      name: 'paddingTop',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    },
    {
      name: 'paddingBottom',
      type: 'text',
      enum: ['default', 'reduced'],
      defaultValue: 'reduced'
    }
  ]
})
</script>
